import React, { useEffect, useState, useRef } from 'react';
import storageAvailable from '../../helpers/storage_available';
import Map from './components/Map';
import FilterOptions from './components/FilterOptions';
import CurrentSearchOptions from './components/CurrentSearchOptions';
import BroadenSearchOptions from './components/BroadenSearchOptions';
import searchApi from './components/searchApi';
import processResults from './components/processResults';
import addTrackingToBackLink from './helpers/addTrackingToBackLink';
import to_upper_case_first from '../../helpers/to_upper_case_first';
import RoomTypeOptions from './components/RoomTypeOptions';
import trackEvent from '../../helpers/gtm/eventPush';

let previousResults = [];

const objectMatches = (obj, source) =>
  Object.keys(source).every(
    (key) =>
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] === source[key],
  );

const objectsMatch = (object1, object2) =>
  objectMatches(object1, object2) && objectMatches(object2, object1);

function numberOrZero(value) {
  if (!Number.isNaN(parseInt(value, 10))) {
    return parseInt(value, 10);
  }
  return 0;
}

const defaultRoomTypeArray = [
  'showme_rooms',
  'showme_1beds',
  'showme_buddyup_properties',
];

export default function SearchMap({
  initialSearchData: {
    api_params,
    search_params,
    location_info,
    search_id,
    browse_url,
    max_per_page_list_view,
    where_hash,
  },
  mode,
  editing,
  useAppleMaps,
  appleMapsToken,
  siteData: {
    domain_name: domain,
    id: site_id,
    sub_dir: subDir,
    base_url,
    static_files_server: staticFilesServer,
    offered_detail_url: offeredDetailUrl,
    currency_symbol: currencySymbol,
    country,
  },
  isInteractiveMap,
  resultsPageOffset,
  localisations,
}) {
  const [isInitialSearch, updateIsInitialSearch] = useState(true);
  const [isLoading, updateIsLoading] = useState(true);
  const [currentSearch, updateCurrentSearch] = useState(null);
  const [availableFilters, updateAvailableFilters] = useState(null);
  const [totalResults, updateTotalResults] = useState(0);
  const [returnedResults, updateReturnedResults] = useState(0);
  const [results, updateResults] = useState([]);
  const [isInteractive, updateIsInteractive] = useState(
    isInteractiveMap || false,
  );
  const [location, updateLocation] = useState(location_info);
  const [useSearchId, updateUseSearchId] = useState(!!search_id);
  const [mapLoaded, updateMapLoaded] = useState(false);
  const [searchParams, updateSearchParams] = useState(setInitialSearchParams);
  const [broadenSearchOptions, updateBroadenSearchOptions] = useState(null);
  const [backLink, updateBackLink] = useState(getBackToMapUrl());
  const [activeRoomTypes, updateActiveRoomTypes] = useState(
    setInitialRoomTypes,
  );

  function setInitialSearchParams() {
    const initialSearchParams = {
      ...search_params,
    };
    const initialRoomTypes = defaultRoomTypeArray.filter(
      (room_type) => search_params[room_type],
    );
    if (initialRoomTypes.length == 3) {
      // remove all room types from params
      Object.keys(initialSearchParams).forEach((key) => {
        if (defaultRoomTypeArray.includes(key)) {
          delete initialSearchParams[key];
        }
      });
    }
    return initialSearchParams;
  }

  function setInitialRoomTypes() {
    const initialRoomTypes = defaultRoomTypeArray.filter(
      (room_type) => search_params[room_type],
    );
    return (
      (initialRoomTypes.length && initialRoomTypes) || defaultRoomTypeArray
    );
  }

  function getRoomTypesAsParams(room_array) {
    const roomTypes = room_array.reduce((o, key) => ({ ...o, [key]: 'Y' }), {});
    return roomTypes;
  }

  function getActiveRoomTypesAsParams() {
    return getRoomTypesAsParams(activeRoomTypes);
  }

  function addRoomTypesToSearchParams() {
    return {
      ...searchParams,
      ...getActiveRoomTypesAsParams(),
    };
  }

  function roomTypeHasChanged() {
    const initialRoomTypes = defaultRoomTypeArray.filter(
      (room_type) => search_params[room_type],
    );
    return (
      initialRoomTypes.sort().toString() == activeRoomTypes.sort().toString()
    );
  }

  // this may need removing or simply becomes a proxy for adding or removing filter
  function toggleRoomType(roomType, isActive) {
    if (isLoading) {
      return false;
    }
    return isActive
      ? removeParamsFromSearch([roomType])
      : addParamsToSearch({ [roomType]: 'Y' });
  }

  function trackInteractiveMapCheckBox() {
    trackEvent({
      category: 'narrow',
      action: 'map_view_toggle_interactive',
      label: isInteractive ? 'on' : 'off',
    });
  }

  function getBackToMapUrl() {
    let path = `/${subDir}/`;
    const params = new URLSearchParams();
    if (search_id) {
      const paramsObject = {
        search_id,
        mode,
        editing,
      };
      Object.keys(paramsObject).forEach((key) =>
        params.set(key, paramsObject[key]),
      );
    } else if (browse_url) {
      path = browse_url;
      Object.keys(searchParams).forEach((key) =>
        params.set(key, searchParams[key]),
      );
      params.delete('where');
    }
    params.set('show_results', 'as a map');
    params.set('resume_last_search', 1);
    return `${path}?${params.toString()}`;
  }

  function constructTheBackLink() {
    let path = `/${subDir}/`;
    const params = new URLSearchParams();
    if (search_id) {
      params.set('search_id', search_id);
      if (!useSearchId || searchHasChanged()) {
        path = `${path}search_copy_n_edit.pl`;
        params.set('replace_all_adv_params', 1);
        Object.keys(searchParams).forEach((key) =>
          params.set(key, searchParams[key]),
        );
        if (roomTypeHasChanged()) {
          const roomTypes = getActiveRoomTypesAsParams();
          Object.keys(roomTypes).forEach((key) =>
            params.set(key, roomTypes[key]),
          );
        }
      }
      if (resultsPageOffset && resultsPageOffset > 0 && !searchHasChanged()) {
        params.set('offset', resultsPageOffset);
      }
    } else if (browse_url) {
      Object.keys(searchParams).forEach((key) =>
        params.set(key, searchParams[key]),
      );
      params.delete('where');
      params.delete('flatshare_type');
      params.delete('location_type');
      path = browse_url;
      if (resultsPageOffset && resultsPageOffset > 0 && !searchHasChanged()) {
        path = `${browse_url}/page${parseInt(resultsPageOffset, 10) /
          (max_per_page_list_view || 10) +
          1}`;
      }
    }
    if (max_per_page_list_view) {
      params.set('max_per_page', max_per_page_list_view);
    }
    if (editing) {
      params.set('mode', mode);
      params.set('editing', editing);
    }
    params.sort();
    return `${path}${
      [...new Set(params.keys())].length ? `?${params.toString()}` : ''
    }`;
  }

  function updateTheBackLink() {
    return updateBackLink(constructTheBackLink());
  }

  function handleNewResults(newResults) {
    previousResults = [...results];
    const processedResults = newResults
      ? processResults(
          newResults,
          {
            subDir,
            offeredDetailUrl,
            currencySymbol,
            searchResults: getBackToMapUrl(),
          },
          localisations,
        )
      : [];
    updateResults(processedResults);
  }

  // params = object
  function addParamsToSearch(params) {
    const roomType = defaultRoomTypeArray.filter((room_type_param) =>
      Object.keys(params).includes(room_type_param),
    );
    if (roomType.length) {
      updateUseSearchId(false);

      if (activeRoomTypes.length == 2) {
        const new_params = { ...searchParams };
        activeRoomTypes.forEach((param) => delete new_params[param]);
        return updateSearchParams(new_params);
      }
    }
    return updateSearchParams((previousState) => ({
      ...previousState,
      ...params,
    }));
  }

  // params = array
  function removeParamsFromSearch(params) {
    let isRoomTypeParam = false;
    if (
      defaultRoomTypeArray.some((room_type_param) =>
        params.includes(room_type_param),
      )
    ) {
      isRoomTypeParam = true;
      updateUseSearchId(false);
    }
    if (
      useSearchId &&
      params.filter((param) => Object.keys(search_params).includes(param))
    ) {
      updateUseSearchId(false);
    }
    return updateSearchParams((previousState) => {
      params.forEach((param) => delete previousState[param]);
      return {
        ...previousState,
        ...(isRoomTypeParam &&
          activeRoomTypes.length == 3 &&
          getRoomTypesAsParams(
            activeRoomTypes.filter((type) => type != params[0]),
          )),
      };
    });
  }

  function handleAddFilter(event, params) {
    event.preventDefault();
    if (!isLoading) {
      addParamsToSearch(params);
    }
  }

  function handleRemoveFilter(event, params) {
    event.preventDefault();
    if (!isLoading) {
      removeParamsFromSearch(params);
    }
  }

  function clearFilters(event) {
    event.preventDefault();
    if (isLoading) {
      return false;
    }
    updateUseSearchId(false);
    const params = currentSearch
      .filter((group) => {
        if (group.group_name.match(/What|Where|Include|Inclure/i)) {
          return false;
        }
        return true;
      })
      .flatMap((group) =>
        group.group_options.flatMap((option) => option.remove_params),
      );
    return updateSearchParams((previousState) => {
      [...params, ...defaultRoomTypeArray].forEach(
        (param) => delete previousState[param],
      );
      return {
        ...previousState,
      };
    });
  }

  function handleClickBroaden(event, removeParams, addParams) {
    event.preventDefault();
    if (!isLoading) {
      if (removeParams) {
        removeParamsFromSearch(removeParams);
      }
      if (addParams) {
        addParamsToSearch(addParams);
      }
    }
  }

  function search() {
    updateIsLoading(true);

    if (isInitialSearch) {
      return makeInitialSearch();
    }
    let params = {
      ...api_params,
      ...searchParams,
      ...location,
      interactive: isInteractive ? 1 : 0,
    };
    if (useSearchId) {
      params = { ...params, search_id };
    }
    return searchApi(params);
  }

  function makeInitialSearch() {
    updateTheBackLink();
    if (api_params.resume_search && storageAvailable('sessionStorage')) {
      const storedSearch = sessionStorage.getItem(
        `resume_search_${where_hash}`,
      );
      if (storedSearch) {
        return searchApi({
          ...api_params,
          ...JSON.parse(storedSearch),
          interactive: isInteractive ? 1 : 0,
          ...location,
        });
      }
    }
    if (useSearchId) {
      return searchApi({
        ...api_params,
        // ...location,
        search_id,
        interactive: isInteractive ? 1 : 0,
      });
    }
    return searchApi({
      ...api_params,
      ...searchParams,
    });
  }

  function searchHasChanged() {
    return !objectsMatch(search_params, addRoomTypesToSearchParams());
  }

  function handleResponse({
    your_search,
    filter_options,
    count,
    no_displayed,
    results: newResults,
    broaden_search_options,
  }) {
    updateCurrentSearch(your_search);
    updateAvailableFilters(filter_options);
    updateTotalResults(numberOrZero(count));
    updateReturnedResults(numberOrZero(no_displayed));
    handleNewResults(newResults);
    updateBroadenSearchOptions(broaden_search_options);
    updateActiveRoomTypes(
      your_search
        .filter((group, key) => group.group_name.match(/Include|Inclure/i))[0]
        .group_options.flatMap((option) => option.remove_params),
    );
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem(
        `resume_search_${where_hash}`,
        JSON.stringify(searchParams),
      );
    }
    updateIsLoading(false);
    if (isInitialSearch) {
      updateIsInitialSearch(false);
    } else {
      updateTheBackLink();
    }
  }

  function setInteractiveMapStorage() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('interactiveMap', isInteractive);
    }
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    async function makeSearch() {
      const response = await search();
      handleResponse(response);
    }
    makeSearch();
  }, [searchParams]);

  useEffect(() => {
    async function makeSearch() {
      const response = await search();
      handleResponse(response);
    }
    if (!objectsMatch(location, location_info)) {
      updateUseSearchId(false);
      makeSearch();
    }
  }, [location]);

  useEffect(() => {
    async function makeSearch() {
      const response = await search();
      handleResponse(response);
    }
    if (isInteractive && !isInitialSearch && searchHasChanged()) {
      makeSearch();
    }
    if (!isInitialSearch) {
      setInteractiveMapStorage();
    }
    trackInteractiveMapCheckBox();
  }, [isInteractive]);
  /* eslint-enable react-hooks/exhaustive-deps */
  return (
    <div className="search-map">
      <div className="search-options">
        <div className="panel panel-header-closed search-widget panel-standard">
          <header className="">
            <h3 className="">
              {to_upper_case_first(
                `${localisations.search} ${localisations.rooms_for_rent}`.toLowerCase(),
              )}
            </h3>
          </header>
          <div className="search-options__details">
            <ul className="search-options__links">
              <li className="search-options__link">
                {/* resultUrl */}
                <a href={backLink} onClick={addTrackingToBackLink}>
                  {localisations.back_to_results_list}
                </a>
              </li>
              <li className="search-options__link">
                <a href={`/${subDir}/search.pl`}>
                  {localisations.start_new_search}
                </a>
              </li>
            </ul>
          </div>
          <RoomTypeOptions
            activeRoomTypes={activeRoomTypes}
            toggleRoomType={toggleRoomType}
            localisations={localisations}
          />
          <CurrentSearchOptions
            searchOptions={currentSearch}
            handleRemoveFilter={handleRemoveFilter}
          />
        </div>
        <FilterOptions
          availableFilters={availableFilters}
          handleAddFilter={handleAddFilter}
          localisations={localisations}
          clearFilters={clearFilters}
          // Don't show for appartager until tiltes have been localised
          showFilterGroupName={site_id != 21}
        />
      </div>
      <div className="search-map__map-wrapper">
        <div className="search-map__header">
          {isLoading ? (
            <p className="search-map__info">
              <i aria-hidden="true" className="far fa-spinner fa-pulse"></i>{' '}
              {/* Below is a nasty quick fix for the localisation */}
              {to_upper_case_first(localisations.loading)}
              {site_id == 21 && ' des'} {localisations.results}
            </p>
          ) : (
            <div
              className="search-map__info"
              aria-busy={isLoading}
              aria-live={!isLoading && !isInitialSearch ? 'polite' : 'off'}
            >
              {/* Needs localising */}
              {site_id != 21 && 'Showing '}
              {returnedResults} {localisations.of} {totalResults}
            </div>
          )}
          <div className="search-map__interactive-toggle">
            <input
              type="checkbox"
              id="interactive_map"
              checked={isInteractive}
              onChange={() => updateIsInteractive(!isInteractive)}
            />
            <label htmlFor="interactive_map">
              {localisations.search_where_map_dragged}
            </label>
          </div>
        </div>
        <Map
          base_url={base_url}
          domain={domain}
          isInteractive={isInteractive}
          location={location}
          results={results}
          previousResults={previousResults}
          staticFilesServer={staticFilesServer}
          updateLocation={updateLocation}
          updateMapLoaded={updateMapLoaded}
          useAppleMaps={useAppleMaps}
          appleMapsToken={appleMapsToken}
          isLoading={isLoading}
          country={country.toLowerCase()}
          localisations={localisations}
        ></Map>
        <BroadenSearchOptions
          broadenSearchOptions={broadenSearchOptions}
          handleClickBroaden={handleClickBroaden}
          totalResults={totalResults}
          localisations={localisations}
        />
      </div>
    </div>
  );
}
